import { useEffect, useState } from 'react';
import { Loader } from '@mantine/core';
import { ChatImageLightbox } from '@shared/ui/ChatImageLightbox';

const IMG_GAP = 2;
/** Компактнее мобилки: в админке панель шире, не нужно на полэкрана */
const MSG_MAX_W = 260;
const VERT_SINGLE_W = 140;

type ImgMeta = { url: string; w: number; h: number; vertical: boolean };

type ImgCell = {
  url: string;
  width: number;
  height: number;
  objectFit: 'cover' | 'contain';
};

function naturalHeight(meta: ImgMeta, width: number) {
  return Math.round(width * (meta.h / meta.w));
}

/** Два фото в ряд на всю ширину, одна высота, без пустот */
function pairRow(a: ImgMeta, b: ImgMeta): ImgCell[] {
  const total = MSG_MAX_W - IMG_GAP;
  const aspectA = a.w / a.h;
  const aspectB = b.w / b.h;
  const height = total / (aspectA + aspectB);
  const widthA = Math.floor(height * aspectA);
  const widthB = total - widthA;
  const h = Math.round(height);
  return [
    { url: a.url, width: widthA, height: h, objectFit: 'contain' },
    { url: b.url, width: widthB, height: h, objectFit: 'contain' },
  ];
}

function fullRow(m: ImgMeta, allowCrop = false): ImgCell[] {
  const maxH = Math.round(MSG_MAX_W * 1.2);
  let height = naturalHeight(m, MSG_MAX_W);
  let objectFit: 'cover' | 'contain' = 'contain';
  if (allowCrop && height > maxH) {
    height = maxH;
    objectFit = 'cover';
  }
  return [{ url: m.url, width: MSG_MAX_W, height, objectFit }];
}

function layoutChatImages(metas: ImgMeta[]): ImgCell[][] {
  if (metas.length === 0) return [];

  if (metas.length === 1) {
    const m = metas[0];
    const width = m.vertical ? VERT_SINGLE_W : MSG_MAX_W;
    return [[{ url: m.url, width, height: naturalHeight(m, width), objectFit: 'contain' }]];
  }

  if (metas.length === 2) {
    return [pairRow(metas[0], metas[1])];
  }

  if (metas.length === 3) {
    const verts = metas.filter((m) => m.vertical);
    const horizs = metas.filter((m) => !m.vertical);

    if (verts.length === 1 && horizs.length === 2) {
      return [fullRow(verts[0], true), pairRow(horizs[0], horizs[1])];
    }

    if (verts.length >= 2) {
      return [
        pairRow(verts[0], verts[1]),
        fullRow(metas.find((m) => m !== verts[0] && m !== verts[1]) ?? metas[2], true),
      ];
    }

    return [pairRow(metas[0], metas[1]), fullRow(metas[2], true)];
  }

  const rows: ImgCell[][] = [];
  for (let i = 0; i < metas.length; ) {
    if (i === metas.length - 1) {
      rows.push(fullRow(metas[i], true));
      break;
    }
    rows.push(pairRow(metas[i], metas[i + 1]));
    i += 2;
  }
  return rows;
}

function loadImageMeta(url: string): Promise<ImgMeta> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 1;
      const h = img.naturalHeight || 1;
      resolve({ url, w, h, vertical: h > w });
    };
    img.onerror = () => {
      resolve({ url, w: 4, h: 3, vertical: false });
    };
    img.src = url;
  });
}

type ChatBubbleImagesProps = {
  urls: string[];
  /** CSS-префикс классов сетки, по умолчанию как в чате с клиентами */
  classPrefix?: string;
};

/** Сетка фото как в мобильном чате: ряды без пустого пространства. */
export function ChatBubbleImages({
  urls,
  classPrefix = 'client-support-chat-panel',
}: ChatBubbleImagesProps) {
  const [rows, setRows] = useState<ImgCell[][] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const list = urls.filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    if (list.length === 0) {
      setRows([]);
      return undefined;
    }
    setRows(null);
    void Promise.all(list.map(loadImageMeta)).then((metas) => {
      if (!cancelled) setRows(layoutChatImages(metas));
    });
    return () => {
      cancelled = true;
    };
  }, [urls.join('|')]);

  if (!rows) {
    return (
      <div className={`${classPrefix}__bubble-images-loading`}>
        <Loader size="xs" />
      </div>
    );
  }

  if (rows.length === 0) return null;

  const contentWidth = Math.max(
    ...rows.map(
      (row) =>
        row.reduce((sum, cell) => sum + cell.width, 0) +
        IMG_GAP * Math.max(0, row.length - 1),
    ),
  );

  return (
    <>
      <div
        className={`${classPrefix}__bubble-images`}
        style={{ width: contentWidth }}
      >
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className={`${classPrefix}__bubble-images-row`}>
            {row.map((cell) => {
              const imageIndex = list.indexOf(cell.url);
              return (
                <button
                  key={cell.url}
                  type="button"
                  className={`${classPrefix}__bubble-image-link`}
                  style={{ width: cell.width, height: cell.height }}
                  onClick={() =>
                    setLightboxIndex(imageIndex >= 0 ? imageIndex : 0)
                  }
                >
                  <img
                    src={cell.url}
                    alt=""
                    className={`${classPrefix}__bubble-image`}
                    style={{ width: cell.width, height: cell.height, objectFit: cell.objectFit }}
                  />
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {lightboxIndex != null ? (
        <ChatImageLightbox
          urls={list}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </>
  );
}
